/** Pure helpers for the Invoices screens: no fetch, no React. */

export const LIST_STATES = ['needs_review', 'awaiting_approval', 'auto', 'approved', 'rejected'];

export const STATE_LABELS = {
  needs_review: 'Needs review',
  awaiting_approval: 'Waiting for approval',
  auto: 'Posted',
  approved: 'Posted',
  rejected: 'Rejected',
};

export const STATE_TONE = {
  needs_review: 'bg-accent-50 text-accent-800 border-accent-200',
  awaiting_approval: 'bg-warn/10 text-ink border-warn/40',
  auto: 'bg-primary-50 text-primary-700 border-primary-200',
  approved: 'bg-primary-50 text-primary-700 border-primary-200',
  rejected: 'bg-surface-sunken text-ink-muted border-surface-border',
};

/** A read this long is slow, not failed: only the server says a file failed. */
export const SLOW_READ_MS = 15 * 60 * 1000;
/** A row in one of these states needs nothing more from the person, so it clears itself shortly. */
export const SETTLED_UPLOAD_KEYS = ['auto', 'approved', 'rejected', 'removed'];

export function stateLabel(state) {
  return STATE_LABELS[state] || state || 'Unknown';
}

export function elapsedLabel(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const READING_STAGE = {
  extract: 'reading the document',
  validate: 'checking the totals',
  resolve: 'matching items',
  categorize: 'sorting categories',
};

/**
 * Where one uploaded file is now. The invoice's own state wins once it exists; before that the server's progress
 * answer says whether the file is queued (and behind how many), being read, failed, or gone. Without an answer
 * (offline, first poll) it falls back to the browser's own clock: uploaded, then reading.
 */
export function uploadStage(upload, invoicesByDoc, now = Date.now(), progress = {}) {
  if (upload.error) return { key: 'failed', label: 'Failed', reason: upload.error };
  if (upload.duplicate) return { key: 'failed', label: 'Failed', reason: 'Already uploaded — this exact file is in the list' };
  const inv = upload.documentId ? invoicesByDoc[upload.documentId] : null;
  if (inv) {
    const errs = inv.extraction_errors || [];
    const resend = inv.duplicate_of && !['auto', 'approved', 'rejected'].includes(inv.state)
      ? 'Looks like a re-send of an invoice already received: check it before approving' : null;
    return { key: inv.state, label: stateLabel(inv.state), invoiceId: inv.id,
      reason: errs.length ? readableErrors(errs) : resend };
  }
  if (!upload.documentId) return { key: 'uploading', label: 'Uploading' };
  const p = progress[upload.documentId];
  if (p && p.found === false) return { key: 'removed', label: 'Removed', reason: 'This file was removed, so there is nothing to wait for.' };
  if (p && p.status === 'failed') {
    return { key: 'failed', label: 'Failed',
      reason: p.error ? `Could not be read (${p.error}) — try a clearer photo or a PDF` : 'Could not be read — try a clearer photo or a PDF' };
  }
  const waited = elapsedLabel(now - upload.startedAt);
  if (p && p.status === 'received') {
    const ahead = p.queued_ahead || 0;
    return { key: 'queued', label: 'Queued',
      detail: `${ahead ? `${ahead} file${ahead === 1 ? '' : 's'} ahead` : 'next up'} · waiting ${waited}` };
  }
  if (p && p.status === 'processing') {
    const step = READING_STAGE[p.stage];
    return { key: 'reading', label: 'Reading', detail: `${step ? `${step} · ` : ''}${waited}` };
  }
  if (now - upload.startedAt > SLOW_READ_MS) {
    return { key: 'reading', label: 'Reading', detail: `taking longer than usual · ${waited}` };
  }
  return upload.polled ? { key: 'reading', label: 'Reading', detail: waited } : { key: 'uploaded', label: 'Uploaded' };
}

const ERROR_TEXT = {
  missing_supplier: 'supplier not found',
  missing_date: 'date not found',
  no_lines: 'no lines found',
  totals_mismatch: 'lines do not add up to the total',
  bad_vat: 'VAT could not be read',
};

export function readableErrors(errors) {
  return (errors || []).map((e) => ERROR_TEXT[e] || e).join(', ');
}

export function num(value) {
  if (value === null || value === undefined || value === '' || value === 'None') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

export function money(value) {
  const n = num(value);
  return n === null ? '—' : GBP.format(Math.abs(n) < 0.005 ? 0 : n);  // never "-£0.00"
}

/** Lines + VAT against the printed total. Green only when they agree to the penny (2p rounding). */
export function reconcile(invoice) {
  const lines = invoice?.lines || [];
  const net = Math.round(lines.reduce((s, l) => s + (num(l.line_total) || 0), 0) * 100) / 100;
  const vat = num(invoice?.totals?.vat_total);
  const total = num(invoice?.totals?.invoice_total);
  const expected = Math.round((net + (vat || 0)) * 100) / 100;
  if (total === null || lines.length === 0) return { net, vat, total, expected, addsUp: null, gap: null };
  const gap = Math.round((total - expected) * 100) / 100;
  return { net, vat, total, expected, addsUp: Math.abs(gap) <= 0.02, gap };
}

/** Show per-gram / per-ml prices per kg / per litre so they read sensibly. */
export function unitPrice(value, unit) {
  const n = num(value);
  if (n === null) return { value: null, unit };
  if (unit === 'g') return { value: n * 1000, unit: 'kg' };
  if (unit === 'ml') return { value: n * 1000, unit: 'l' };
  return { value: n, unit };
}

/** Pair each line with its open review tasks (by raw_desc, in order) and give it a match status. */
export function buildLineViews(invoice, tasks) {
  const open = (tasks || []).filter((t) => t.status === 'open');
  const used = new Set();
  const take = (type, raw) => {
    const t = open.find((x) => x.type === type && !used.has(x.id) && (x.payload?.line?.raw_desc || '') === raw);
    if (t) used.add(t.id);
    return t || null;
  };
  return (invoice?.lines || []).map((line, index) => {
    const raw = line.raw_desc || line.description || '';
    const skuTask = take('sku_match', raw);
    const glTask = skuTask ? null : take('gl_category', raw);
    let status = 'matched';
    if (line.not_costed) status = 'not_costed';
    else if (skuTask) status = 'needs_match';
    else if (glTask) status = 'needs_category';
    else if (!line.item_id) status = 'unmatched';
    const suggestions = (line.suggestions || skuTask?.payload?.line?.suggestions || []).filter((s) => s.item_id).slice(0, 3);
    return { index, raw, line, skuTask, glTask, status, suggestions };
  });
}

export function openTaskCount(tasks) {
  return (tasks || []).filter((t) => t.status === 'open').length;
}

/** Old → new price for one item. % only when both prices are on the same unit. */
export function priceChange(oldPrice, oldUnit, newPrice, newUnit) {
  const o = num(oldPrice);
  const n = num(newPrice);
  const sameUnit = !oldUnit || !newUnit || String(oldUnit) === String(newUnit);
  if (o === null || n === null || o === 0 || !sameUnit) return { pct: null, comparable: o !== null && sameUnit };
  return { pct: Math.round(((n - o) / o) * 1000) / 10, comparable: true };
}

export function pctLabel(pct) {
  if (pct === null || pct === undefined) return '';
  if (pct === 0) return 'no change';
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

export const NOT_COSTED_REASONS = [
  { value: 'consumable', label: 'Consumable (bags, foil, cleaning)' },
  { value: 'not_stocked', label: 'Not something we stock' },
  { value: 'adjustment', label: 'Discount or adjustment' },
  { value: 'other', label: 'Other' },
];

export const MATCH_LABELS = {
  matched: 'Matched',
  needs_match: 'Needs a match',
  needs_category: 'Needs a category',
  not_costed: 'Not costed',
  unmatched: 'Not matched',
};
